import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { rentalAPI, errText, aud } from '../../lib/rentalRegisterAPI';
import { PropertyTable } from './PropertyTable';

export const NewPropertyDialog = ({ open, onClose, clientId, meta, onDone }) => {
  const [f, setF] = useState({ address_line1: '', suburb: '', state: '', postcode: '', property_type: 'Residential', date_acquired: '', pct: 100 });
  const go = async () => {
    try {
      const body = { ...f, owners: clientId ? [{ client_id: clientId, owner_type: Number(f.pct) < 100 ? 'Joint' : 'Client', percentage: Number(f.pct) }] : [] };
      const r = await rentalAPI.create(body);
      if (r.warning) toast.warning(r.warning); else toast.success('Property added');
      onClose(); onDone?.(r.property.id);
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}><DialogContent className="max-w-md" data-testid="rental-new-dialog">
      <DialogHeader><DialogTitle>Add rental property</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <Input className="col-span-2" placeholder="Street address" value={f.address_line1} onChange={(e) => setF({ ...f, address_line1: e.target.value })} data-testid="rental-new-address" />
        <Input placeholder="Suburb" value={f.suburb} onChange={(e) => setF({ ...f, suburb: e.target.value })} data-testid="rental-new-suburb" />
        <div className="flex gap-2"><Input placeholder="State" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} /><Input placeholder="Postcode" value={f.postcode} onChange={(e) => setF({ ...f, postcode: e.target.value })} /></div>
        <select className="border rounded h-9 px-2" value={f.property_type} onChange={(e) => setF({ ...f, property_type: e.target.value })} data-testid="rental-new-type">{(meta?.property_types || []).map((t) => <option key={t}>{t}</option>)}</select>
        <Input type="date" value={f.date_acquired} onChange={(e) => setF({ ...f, date_acquired: e.target.value })} data-testid="rental-new-acquired" />
        {clientId && <label className="col-span-2 flex items-center gap-2">Client ownership %<Input className="w-24" type="number" value={f.pct} onChange={(e) => setF({ ...f, pct: e.target.value })} data-testid="rental-new-pct" /></label>}
      </div>
      <DialogFooter><Button disabled={!f.address_line1.trim()} onClick={go} data-testid="rental-new-submit">Add property</Button></DialogFooter>
    </DialogContent></Dialog>
  );
};

const TaxReturns = ({ clientId, canEdit, meta }) => {
  const [rows, setRows] = useState([]);
  const [fy, setFy] = useState('');
  const load = useCallback(() => rentalAPI.taxReturns(clientId).then(setRows).catch(() => {}), [clientId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (meta?.current_fy) { const y = Number(meta.current_fy.slice(0, 4)) - 1; setFy(`${y}-${String(y + 1).slice(2)}`); } }, [meta]);
  const create = async () => { try { await rentalAPI.createTaxReturn(clientId, fy); toast.success(`Tax return ${fy} created`); load(); } catch (e) { toast.error(errText(e)); } };
  return (
    <div className="space-y-2" data-testid="tax-returns-section">
      <div className="flex items-center gap-2"><p className="font-medium text-sm">Tax returns</p>
        {canEdit && <><Input className="h-8 w-24 text-sm" value={fy} onChange={(e) => setFy(e.target.value)} data-testid="tax-return-fy" />
          <Button size="sm" variant="outline" onClick={create} data-testid="tax-return-create">Create tax return for FY</Button></>}</div>
      {rows.length === 0 ? <p className="text-xs text-muted-foreground">No tax returns yet.</p> : (
        <div className="rounded border divide-y text-xs">{rows.map((t) => (
          <Link key={t.id} to={`/rental-properties/tax-returns/${t.id}`} className="flex gap-3 p-2 hover:bg-muted/50" data-testid={`tax-return-${t.financial_year}`}>
            <span className="font-medium w-16">{t.financial_year}</span><Badge variant="outline">{t.status}</Badge><span>{t.entity_type || ''}</span>
            <span className="text-muted-foreground ml-auto">{t.task_ids.length} task(s) · {t.job_ids.length} job(s)</span></Link>))}</div>)}
    </div>
  );
};

export const ClientRentalTab = ({ clientId }) => {
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => rentalAPI.clientProps(clientId).then(setRows).catch((e) => toast.error(errText(e))), [clientId]);
  useEffect(() => { load(); rentalAPI.meta().then(setMeta).catch(() => {}); }, [load]);
  const canEdit = meta?.me?.can_edit;
  const total = (rows || []).reduce((a, r) => a + (r.loan_balance || 0), 0);
  return (
    <div className="space-y-4" data-testid="client-rental-tab">
      <div className="flex items-center gap-2"><p className="font-medium">Rental properties ({rows?.length ?? 0})</p><span className="text-xs text-muted-foreground">Total loan balances {aud(total)}</span>
        {canEdit && <Button size="sm" className="ml-auto" onClick={() => setAdding(true)} data-testid="client-rental-add"><Plus className="w-3.5 h-3.5 mr-1" />Add property</Button>}</div>
      <PropertyTable rows={rows} />
      <TaxReturns clientId={clientId} canEdit={canEdit} meta={meta} />
      <NewPropertyDialog open={adding} onClose={() => setAdding(false)} clientId={clientId} meta={meta} onDone={load} />
    </div>
  );
};
